import LoadingComponent from "../../../components/LoadingComponent.jsx";
import {useEffect, useMemo, useState} from "react";
import {ProjectApi, ProjectLegacyReport} from "../../../api/authority/generated/index";
import useAuthorityConfiguration from "../../../api/utilities/useAuthorityConfiguration.js";
import toast from "react-hot-toast";
import {useErrorTranslation} from "../../../api/utilities/translateErrorMessage.js";
import {useTranslation} from "react-i18next";
import Select from "react-select";
import Toggle from "../../../components/Toggle.jsx";
import Button from "../../../components/Button.jsx";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronDown, faChevronRight} from "@fortawesome/free-solid-svg-icons";
import {hasSubscriptionFeature} from "../../../utils/subscriptionFeatures.js";

function PermissionSection({title, description, activeCount, totalCount, open, onToggle, children}) {
    return <section className="mt-4 overflow-hidden rounded-lg border border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-800/40">
        <button
            type="button"
            onClick={onToggle}
            className={`flex w-full items-center justify-between gap-4 bg-gray-100 px-4 py-4 text-left transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 ${open ? "border-b border-gray-300 dark:border-gray-600" : ""}`}
            aria-expanded={open}
        >
            <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100">{title}</h3>
                    <span className="whitespace-nowrap rounded-full bg-purple-100 px-2 py-0.5 text-xs font-semibold text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">
                        {activeCount} / {totalCount}
                    </span>
                </div>
                <p className="mt-1 text-sm font-normal text-gray-500 dark:text-gray-400">{description}</p>
            </div>
            <FontAwesomeIcon className="shrink-0 text-gray-500" icon={open ? faChevronDown : faChevronRight}/>
        </button>
        {open && <div className="px-4 pb-4">{children}</div>}
    </section>;
}

export default function RolePermissions(
    {
        project,
        roleId,
        onDelete = () => {},
    }
) {
    let {et} = useErrorTranslation();
    let {t} = useTranslation();

    const [refresh, setRefresh] = useState(false);
    const [role, setRole] = useState(null);
    const [pendingRole, setPendingRole] = useState(null);
    const [channels, setChannels] = useState({});
    const [openSections, setOpenSections] = useState({reports: false, legacy: false, settings: false, costs: false, metrics: false});

    const toggleSection = (section) => setOpenSections((current) => ({...current, [section]: !current[section]}));

    useEffect(() => {
        setOpenSections({reports: false, legacy: false, settings: false, costs: false, metrics: false});
    }, [roleId]);

    let authorityConfiguration = useAuthorityConfiguration();

    const reportPermissions = useMemo(() => [
        ['ReportingOverview', t('reporting_lab.pages.overview')],
        ['ReportingDaily', t('reporting_lab.pages.daily')],
        ['ReportingPeriodComparison', t('reporting_lab.pages.period_comparison')],
        ['ReportingOrders', t('reporting_lab.pages.orders')],
        ['ReportingMarketing', t('reporting_lab.pages.marketing')],
        ['ReportingMarketingTrends', t('reporting_lab.pages.marketing_trends')],
        ['ReportingPno', t('reporting_lab.pages.pno')],
        ['ReportingCampaigns', t('reporting_lab.pages.campaigns')],
        ['ReportingProducts', t('reporting_lab.pages.products')],
        ['ReportingAbc', t('reporting_lab.pages.abc')],
        ['ReportingProductProfitability', t('reporting_lab.pages.product_profitability')],
        ['ReportingProfitability', t('reporting_lab.pages.profitability')],
        ['ReportingDiscounts', t('reporting_lab.pages.discounts')],
        ['ReportingDeliveryPayments', t('reporting_lab.pages.delivery_payments')],
        ['ReportingDomains', t('reporting_lab.pages.domains')],
        ['ReportingCentres', t('reporting_lab.pages.centres')],
    ], [t]);

    const legacyReportPermissions = useMemo(() => [
        [ProjectLegacyReport.DailyOverview, t('route.daily-metrics')],
        [ProjectLegacyReport.MainOverview, t('route.performance-summary')],
        [ProjectLegacyReport.MarketingAndSales, t('route.marketing-and-sales')],
        [ProjectLegacyReport.DiscountsAndCoupons, t('route.sales-promotions')],
        [ProjectLegacyReport.CampaignPerformance, t('route.campaign-performance')],
        [ProjectLegacyReport.CampaignComparison, t('route.campaign-comparison')],
    ], [t]);

    const updateRole = (nextRole) => {
        setRole(nextRole);
        setPendingRole(nextRole);
    };

    const updateRoleValue = (key, value) => {
        updateRole({...role, [key]: value});
    };

    let permissions = useMemo(() => {
        let subscription = project.subscription;

        return [
            {
                key: 'allowsProfitability',
                section: 'reports',
                title: t('route.campaign-profitability'),
            },
            {
                key: 'allowsProjectProfile',
                section: 'settings',
                title: t('route.project-settings'),
            },
            {
                key: 'allowsProjectOrderStatuses',
                section: 'settings',
                title: t('project.order-statuses'),
            },
            {
                key: 'allowsProjectIntegrations',
                section: 'settings',
                title: t('project.role-project-integration-settings', {defaultValue: 'Domény a integrace třetích stran'}),
            },
            {
                key: 'allowsProjectBilling',
                section: 'settings',
                title: t('route.subscription'),
            },
            {
                key: 'allowsProjectAffiliate',
                section: 'costs',
                title: t('route.affiliate-spends'),
                show: hasSubscriptionFeature(subscription, 'affiliate'),
            },
            {
                key: 'allowsProjectMailing',
                section: 'costs',
                title: t('route.mailing-spends'),
                show: hasSubscriptionFeature(subscription, 'emailing'),
            },
            {
                key: 'allowsProjectOfflineSpends',
                section: 'costs',
                title: t('route.other-spends'),
                show: hasSubscriptionFeature(subscription, 'other_costs'),
            },
            {
                key: 'allowsProjectUsers',
                section: 'settings',
                title: t('project.role-project-user-settings', {defaultValue: 'Uživatelé a uživatelské role'}),
            },
            {
                key: 'allowsProjectInvitations',
                section: 'settings',
                title: t('route.project-invitations'),
            },
        ];
    }, [project.id]);

    useEffect(() => {

        if (refresh) {
            setRefresh(false);
            return;
        }

        if (roleId === null) {
            return;
        }

        let active = true;
        setRole(null);
        setChannels({});

        new ProjectApi(authorityConfiguration)
            .getProjectRole({
                projectId: project.id,
                roleId: roleId,
            })
            .then((projectRoleOutput) => {
                if (!active) return;
                setRole(projectRoleOutput.projectRole);
                setChannels(projectRoleOutput.channels);
            })
            .catch((e) => {
                if (active) et(e).then((message) => { if (message) toast.error(message); });
            });

        return () => { active = false; };

    }, [project.id, roleId, refresh]);

    let channelOptions = useMemo(() => {
        let options = [];

        for (let channelKey of Object.keys(channels)) {
            let channelValue = channels[channelKey];

            options.push({
                value: channelKey,
                label: channelValue,
            });
        }

        return options;
    }, [role, project.id, roleId]);

    let channelsDisabled = useMemo(() => {
        let options = [];

        for (let channelKey of Object.keys(channels)) {
            let channelValue = channels[channelKey];

            if (role?.forbiddenChannels?.includes(channelKey)) {
                options.push({
                    value: channelKey,
                    label: channelValue,
                });
            }

        }

        return options;
    }, [role, project.id, roleId]);

    useEffect(() => {
        if (pendingRole === null) {
            return;
        }

        new ProjectApi(authorityConfiguration)
            .patchProjectRole({
                projectId: project.id,
                roleId: roleId,
                patchRoleInput: pendingRole,
            })
            .then(() => {
                setPendingRole(null);
                setRefresh(true);
            })
            .catch((e) => {
                setPendingRole(null);
                et(e).then((message) => { if (message) toast.error(message); });
            });
    }, [pendingRole]);

    const reportSectionPermissions = permissions.filter((permission) => permission.section === 'reports');
    const settingsSectionPermissions = permissions.filter(
        (permission) => permission.section === 'settings' && (permission.show ?? true),
    );
    const costsSectionPermissions = permissions.filter(
        (permission) => permission.section === 'costs' && (permission.show ?? true),
    );
    const currentReportsActive = (role?.allowedReports ?? []).filter(
        (report) => reportPermissions.some(([availableReport]) => availableReport === report),
    ).length + reportSectionPermissions.filter((permission) => role?.[permission.key]).length;
    const legacyReportsActive = (role?.allowedLegacyReports ?? []).filter(
        (report) => legacyReportPermissions.some(([availableReport]) => availableReport === report),
    ).length;
    const settingsActive = settingsSectionPermissions.filter((permission) => role?.[permission.key]).length;
    const costsActive = costsSectionPermissions.filter((permission) => role?.[permission.key]).length
        + Number(role?.allowedReports?.includes('ReportingOtherCosts') ?? false);
    const metricsActive = [role?.allowsProfitMetrics, role?.allowsPurchasePriceMetrics].filter(Boolean).length;

    return <LoadingComponent loading={pendingRole !== null || (roleId !== null && role === null)}>
        {roleId === null && <div className={'pt-6 pb-6 text-sm text-center'}>{t('project.select-role-to-edit')}</div>}
        {roleId !== null && role !== null && <div>
            <div className={'p-6'}>
                <div className={'border-b border-gray-200 dark:border-gray-700/60'}>
                    <h2 className="text-2xl text-gray-800 dark:text-gray-100 font-bold">
                        {t('project.edit-x-role')}
                    </h2>
                    <div className="mb-5 mt-1 truncate text-sm font-semibold text-purple-600 dark:text-purple-400" title={role.name}>
                        {role.name}
                    </div>
                </div>

                <PermissionSection
                    title={t('project.role-section-current-reports', {defaultValue: 'Aktuální přehledy'})}
                    description={t('project.role-section-current-reports-description', {defaultValue: 'Určuje, které aktuální reportingové stránky smí role otevřít.'})}
                    activeCount={currentReportsActive}
                    totalCount={reportPermissions.length + reportSectionPermissions.length}
                    open={openSections.reports}
                    onToggle={() => toggleSection('reports')}
                >
                <ul>
                    {reportPermissions.map(([report, title]) => <li
                        className="flex justify-between items-center py-3 border-b border-gray-200 last:border-b-0 dark:border-gray-700/60"
                        key={report}
                    >
                        <div className="min-w-0 flex-1 pr-4">
                            <div className="text-gray-800 dark:text-gray-100 font-semibold">{title}</div>
                        </div>
                        <div className="ml-4 flex shrink-0 items-center">
                            <Toggle
                                enabled={role.allowedReports?.includes(report) ?? false}
                                setEnabled={(enabled) => updateRoleValue(
                                    'allowedReports',
                                    enabled
                                        ? [...new Set([...(role.allowedReports ?? []), report])]
                                        : (role.allowedReports ?? []).filter((item) => item !== report),
                                )}
                            />
                        </div>
                    </li>)}
                    {reportSectionPermissions.map((permission) => <li
                        className="flex justify-between items-center border-b border-gray-200 py-3 last:border-b-0 dark:border-gray-700/60"
                        key={permission.key}
                    >
                        <div className="min-w-0 flex-1 pr-4">
                            <div className="font-semibold text-gray-800 dark:text-gray-100">{permission.title}</div>
                            {permission.description && <div className="text-sm">{permission.description}</div>}
                        </div>
                        <div className="ml-4 flex shrink-0 items-center">
                            <Toggle enabled={role[permission.key]} setEnabled={(enabled) => updateRoleValue(permission.key, enabled)}/>
                        </div>
                    </li>)}
                </ul>
                </PermissionSection>

                <PermissionSection
                    title={t('project.role-section-legacy-reports', {defaultValue: 'Starší přehledy'})}
                    description={t('project.role-section-legacy-reports-description', {defaultValue: 'Řídí přístup ke starším přehledům zachovaným kvůli kompatibilitě a zavedeným pracovním postupům.'})}
                    activeCount={legacyReportsActive}
                    totalCount={legacyReportPermissions.length}
                    open={openSections.legacy}
                    onToggle={() => toggleSection('legacy')}
                >
                    <ul>
                        {legacyReportPermissions.map(([report, title]) => <li
                            className="flex items-center justify-between border-b border-gray-200 py-3 last:border-b-0 dark:border-gray-700/60"
                            key={report}
                        >
                            <div className="min-w-0 flex-1 pr-4">
                                <div className="font-semibold text-gray-800 dark:text-gray-100">{title}</div>
                            </div>
                            <div className="ml-4 flex shrink-0 items-center">
                                <Toggle
                                    enabled={role.allowedLegacyReports?.includes(report) ?? false}
                                    setEnabled={(enabled) => updateRoleValue(
                                        'allowedLegacyReports',
                                        enabled
                                            ? [...new Set([...(role.allowedLegacyReports ?? []), report])]
                                            : (role.allowedLegacyReports ?? []).filter((item) => item !== report),
                                    )}
                                />
                            </div>
                        </li>)}
                    </ul>
                </PermissionSection>

                <PermissionSection
                    title={t('project.role-section-settings', {defaultValue: 'Nastavení a správa'})}
                    description={t('project.role-section-settings-description', {defaultValue: 'Řídí přístup ke stránkám nastavení a správě projektu.'})}
                    activeCount={settingsActive}
                    totalCount={settingsSectionPermissions.length}
                    open={openSections.settings}
                    onToggle={() => toggleSection('settings')}
                >
                <ul>
                    {settingsSectionPermissions.map((permission) => {
                        return <li
                            className="flex justify-between items-center py-3 border-b border-gray-200 last:border-b-0 dark:border-gray-700/60"
                            key={permission.key}
                        >
                            <div className="min-w-0 flex-1 pr-4">
                                <div className="text-gray-800 dark:text-gray-100 font-semibold">
                                    {permission.title}
                                </div>
                            </div>
                            <div className="ml-4 flex shrink-0 items-center">
                                <Toggle enabled={role[permission.key]} setEnabled={(enabled) => updateRoleValue(permission.key, enabled)}/>
                            </div>
                        </li>;
                    })}
                </ul>
                </PermissionSection>

                <PermissionSection
                    title={t('project.role-section-costs', {defaultValue: 'Náklady'})}
                    description={t('project.role-section-costs-description', {defaultValue: 'Řídí přístup k přehledu nákladů a formulářům pro jejich zadávání.'})}
                    activeCount={costsActive}
                    totalCount={costsSectionPermissions.length + 1}
                    open={openSections.costs}
                    onToggle={() => toggleSection('costs')}
                >
                    <ul>
                        <li className="flex items-center justify-between border-b border-gray-200 py-3 last:border-b-0 dark:border-gray-700/60">
                            <div className="min-w-0 flex-1 pr-4 font-semibold text-gray-800 dark:text-gray-100">
                                {t('route.costs-overview')}
                            </div>
                            <div className="ml-4 flex shrink-0 items-center">
                                <Toggle
                                    enabled={role.allowedReports?.includes('ReportingOtherCosts') ?? false}
                                    setEnabled={(enabled) => updateRoleValue(
                                        'allowedReports',
                                        enabled
                                            ? [...new Set([...(role.allowedReports ?? []), 'ReportingOtherCosts'])]
                                            : (role.allowedReports ?? []).filter((item) => item !== 'ReportingOtherCosts'),
                                    )}
                                />
                            </div>
                        </li>
                        {costsSectionPermissions.map((permission) => <li
                            className="flex items-center justify-between border-b border-gray-200 py-3 last:border-b-0 dark:border-gray-700/60"
                            key={permission.key}
                        >
                            <div className="min-w-0 flex-1 pr-4 font-semibold text-gray-800 dark:text-gray-100">
                                {permission.title}
                            </div>
                            <div className="ml-4 flex shrink-0 items-center">
                                <Toggle enabled={role[permission.key]} setEnabled={(enabled) => updateRoleValue(permission.key, enabled)}/>
                            </div>
                        </li>)}
                    </ul>
                </PermissionSection>

                <PermissionSection
                    title={t('project.role-section-data-restrictions', {defaultValue: 'Omezení dat a kanálů'})}
                    description={t('project.role-section-data-restrictions-description', {defaultValue: 'Řídí přístup k citlivým metrikám a vybraným marketingovým kanálům ve starých i nových přehledech.'})}
                    activeCount={metricsActive}
                    totalCount={2}
                    open={openSections.metrics}
                    onToggle={() => toggleSection('metrics')}
                >
                <div className="border-b border-gray-200 py-3 dark:border-gray-700">
                    <div className="font-semibold text-gray-800 dark:text-gray-100">{t('project.role-forbidden-channels')}</div>
                    <div className="mt-1 mb-3 text-sm text-gray-500 dark:text-gray-400">
                        {t('project.role-forbidden-channels-description', {defaultValue: 'Skryje vybrané marketingové kanály uživateli této role.'})}
                    </div>
                    <Select
                        className={'custom-select'}
                        menuPortalTarget={typeof document === 'undefined' ? undefined : document.body}
                        styles={{menuPortal: (base) => ({...base, zIndex: 9999})}}
                        options={channelOptions}
                        value={channelsDisabled}
                        onChange={(e) => {
                            updateRole({
                                ...role,
                                forbiddenChannels: e.map((selectedChannel) => selectedChannel.value),
                            });
                        }}
                        isMulti
                    />
                </div>
                <ul>
                    <li className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-gray-700/60">
                        <div className="min-w-0 flex-1 pr-4">
                            <div className="text-gray-800 dark:text-gray-100 font-semibold">{t('project.role-profit-metrics', {defaultValue: 'Zisk a marže'})}</div>
                            <div className="text-sm">{t('project.role-profit-metrics-description', {defaultValue: 'Dovolí zobrazit zisk, marži, POAS a další metriky odvozené od ziskovosti.'})}</div>
                        </div>
                        <div className="ml-4 flex shrink-0 items-center">
                            <Toggle
                                enabled={role.allowsProfitMetrics}
                                disabled={!role.allowsPurchasePriceMetrics}
                                setEnabled={(enabled) => updateRoleValue('allowsProfitMetrics', enabled)}
                            />
                        </div>
                    </li>
                    <li className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-gray-700/60">
                        <div className="min-w-0 flex-1 pr-4">
                            <div className="text-gray-800 dark:text-gray-100 font-semibold">{t('project.role-purchase-price-metrics', {defaultValue: 'Nákupní ceny'})}</div>
                            <div className="text-sm">{t('project.role-purchase-price-metrics-description', {defaultValue: 'Dovolí zobrazit nákupní cenu zboží. Bez tohoto oprávnění se skryjí také marže a POAS, aby cenu nešlo zpětně dopočítat.'})}</div>
                        </div>
                        <div className="ml-4 flex shrink-0 items-center">
                            <Toggle enabled={role.allowsPurchasePriceMetrics} setEnabled={(enabled) => {
                                updateRole({
                                    ...role,
                                    allowsPurchasePriceMetrics: enabled,
                                    ...(!enabled ? {allowsProfitMetrics: false} : {}),
                                });
                            }}/>
                        </div>
                    </li>
                </ul>
                </PermissionSection>
                </div>

            <footer>
                <div className="flex flex-col px-6 py-3 border-t border-gray-200 dark:border-gray-700/60">
                    <div className="flex self-end">
                        <Button
                            variant={'outline-red'}
                            onClick={() => {
                                onDelete(roleId)
                            }}
                        >
                            {t('project.role-delete')}
                        </Button>
                    </div>
                </div>
            </footer>
        </div>
        }
    </LoadingComponent>
        ;
}
